import React, { useEffect, useState } from 'react';
import { IUser } from '../../models';
import { AsyncAction } from '../../utils';
import { IDriver } from '../../models/Driver.model';
import PaneHeadline from '../shared/paneHeadline';
import { faCaretLeft, faUserFriends } from '@fortawesome/free-solid-svg-icons';
import routes from '../../common/routes';
import './drivers.scss';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner2 } from '../shared/spinner2';
import DriversGrid from './DriversGrid';
import DriverModal from './DriverModal';
import { getCustomerIdInUse } from '../../utils/GeneralUtils';
import DriverService from '../../services/DriversService';
import { Toastr } from '../../utils/Toastr';
import { locale } from '../../common/localization/localizationService';
import EventBus from '../../common/EventBus';

export interface IOrderOverviewProps {
  impersonatedUser: any;
  authentication: IUser;
  isLoading: boolean;
  driverList: Array<IDriver>;
  getDrivers: (contractorId: number, ignoreCache?: boolean) => AsyncAction;
}

export default function DriversPage(props: IOrderOverviewProps): JSX.Element {
  const params = new URLSearchParams(document.location.search);
  const contractorId = Number(params.get('contractorId'));
  const customerId = getCustomerIdInUse(props.authentication);

  const [showModal, setShowModal] = useState(false);

  const [selectedDriver, setSelectedDriver] = useState<IDriver>(null);

  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    props.getDrivers(contractorId, true);
  }, []);

  useEffect(() => {
    EventBus.emit('fullScreenChange', true);

    return () => {
      EventBus.emit('fullScreenChange', false);
    };
  }, []);

  const openNewDriverModal = (): void => {
    setShowModal(true);
  };

  const openEditDriverModal = (dataItem: IDriver): void => {
    setSelectedDriver(dataItem);
    setShowModal(true);
  };

  const hideModal = (rerender: boolean): void => {
    setShowModal(false);
    setSelectedDriver(null);
    if (rerender) {
      props.getDrivers(contractorId, true);
    }
  };

  const deleteDriver = async (dataItem: IDriver): Promise<void> => {
    setLoading(true);
    if (confirm(locale.drivers._deleteConfirm + dataItem.driver)) {
      try {
        await DriverService.deleteDriver(
          dataItem.customerId,
          dataItem.contractorId,
          dataItem.driver,
        );
        Toastr.success(locale.drivers._deleted);
        props.getDrivers(contractorId, true);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const existingDrivers = props.driverList.map((driver) => {
    return driver.driver;
  });

  return (
    <>
      <div className="drivers-page">
        <div className="drivers-page-header-row">
          <PaneHeadline
            titleText={locale.drivers._header}
            titleIcon={faUserFriends}
          />
          <div className="driver-page-action-wrapper">
            <Link to={routes.administration.base}>
              <button className="btn btn-outline-dark back-nav-button">
                <FontAwesomeIcon icon={faCaretLeft} />{' '}
                {locale.drivers._contractors}
              </button>
            </Link>
            <button
              className="btn btn-outline-dark open-modal-button"
              onClick={() => openNewDriverModal()}
            >
              {locale.drivers._newDriver}
            </button>
          </div>
        </div>
        <Spinner2 show={props.isLoading || isLoading} />
        <DriversGrid
          className="drivers-grid"
          data={props.driverList}
          deleteDriver={deleteDriver}
          editDriver={openEditDriverModal}
        />
        {showModal && (
          <DriverModal
            contractorId={contractorId}
            customerId={customerId}
            driver={selectedDriver}
            existing={existingDrivers}
            onHide={hideModal}
            show={showModal}
          />
        )}
      </div>
    </>
  );
}
